"use client";
import * as React from "react";
import "@radix-ui/themes/styles.css";
import { Theme } from "@radix-ui/themes";
import "./assets/css/main.css";
import ReduxProvider from "./redux/reduxProvider";
import { Suspense } from "react";
import { AuthProvider } from "./authProvider";
import { initializeMixPanel } from "./services/mixpanelService";

import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Inter } from "next/font/google";
import ToastMessage from "./common/toastMessage";
import Loader from "./loader/page";
import {
  getDataFromSessionStorage,
  removeDataFromSessionStorage,
} from "./services/storageService";
import KeyboardShortcuts from "./common/keyboardShortcuts";
import { createQueryClient } from "./services/queryClient";
import { getAccessToken } from "./services/apiService";
import DynamicTitle from "./DynamicTitle";
import OfflineIndicator from "./common/offlineIndicator/offlineIndicator";
import Script from "next/script";
import { TurnstileProvider } from "./common/turnstileProvider/turnstileProvider";

const inter = Inter({
  subsets: ["latin"],
  display: "swap",
  variable: "--font-inter",
});

export default function RootLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [queryClient] = React.useState(() => createQueryClient());

  React.useEffect(() => {
    // Keep global prefetching minimal to avoid competing network when route loads
    const token = getAccessToken();
    if (!token) return;
    // Intentionally no prefetch here to allow route-driven sequencing
  }, [queryClient]);

  React.useEffect(() => {
    const handleStorageChange = () => {
      if (getDataFromSessionStorage("logout-time")) {
        removeDataFromSessionStorage("logout-time");
        window.location.href = "/"; // or update auth state
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  React.useEffect(() => {
    initializeMixPanel();
  }, []);
  return (
    <html lang="en" className={inter.variable}>
      <body>
        <TurnstileProvider>
          <ReduxProvider>
            <Theme
              accentColor="green"
              grayColor="sand"
              className={`h-full w-auto`}
            >
              <Suspense fallback={<Loader />}>
                <main>
                  <QueryClientProvider client={queryClient}>
                    <AuthProvider>
                      <DynamicTitle />
                      <OfflineIndicator />
                      <ToastMessage
                        title=""
                        message=""
                        isOpen={false}
                        handleToast={null}
                      />
                      {children}
                      <KeyboardShortcuts />
                    </AuthProvider>

                    <ReactQueryDevtools
                      initialIsOpen={false}
                      buttonPosition="bottom-left"
                    />
                  </QueryClientProvider>
                </main>
              </Suspense>
            </Theme>
          </ReduxProvider>
        </TurnstileProvider>
        <Script
          src="https://challenges.cloudflare.com/turnstile/v0/api.js"
          async
          defer
        />
      </body>
    </html>
  );
}
